import { useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { Eye, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { AccountJournal } from "~/types/contabilidad";
import { JOURNAL_TYPE_LABELS } from "~/types/contabilidad";

interface DiarioTableProps {
  journals: AccountJournal[];
  isLoading: boolean;
  onEdit: (journal: AccountJournal) => void;
  onDelete: (id: string) => void;
}

export function DiarioTable({
  journals,
  isLoading,
  onEdit,
  onDelete,
}: DiarioTableProps) {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [journalToDelete, setJournalToDelete] = useState<AccountJournal | null>(
    null
  );

  const handleDeleteClick = (journal: AccountJournal) => {
    setJournalToDelete(journal);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (journalToDelete) {
      onDelete(journalToDelete.id);
      setDeleteDialogOpen(false);
      setJournalToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (journals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">No hay diarios registrados.</p>
        <p className="text-sm text-muted-foreground">
          Crea un nuevo diario para comenzar.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Fecha Creación</TableHead>
              <TableHead className="w-[70px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {journals.map((journal, index) => (
              <TableRow
                key={journal.id}
                className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                onClick={() => navigate(`/contabilidad/diarios/${journal.id}`)}
              >
                <TableCell className="font-mono font-medium">
                  {journal.code}
                </TableCell>
                <TableCell>{journal.name}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {JOURNAL_TYPE_LABELS[journal.type]}
                  </Badge>
                </TableCell>
                <TableCell>{journal.company?.name ?? "-"}</TableCell>
                <TableCell>
                  {dayjs(journal.created_at).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Abrir menú</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(`/contabilidad/diarios/${journal.id}`)
                        }
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Ver Detalle
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEdit(journal)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteClick(journal)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar diario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el diario "{journalToDelete?.name}". Esta
              acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
